import { ApiError } from "@nabble/sdk";
import { useToast } from "@nabble/ui";
import { useState } from "react";
import type { Permission } from "../../../api/adminPermissions";
import { createRole, updateRole, type Role } from "../../../api/adminRoles";
import { errorMessage } from "../../../errors";
import { toggleKeys } from "./permissionGroups";

export interface RoleForm {
  name: string;
  setName: (name: string) => void;
  description: string;
  setDescription: (description: string) => void;
  selected: string[];
  togglePermissions: (permissions: Permission[], on: boolean) => void;
  nameError: string | null;
  error: string | null;
  loading: boolean;
  submit: () => Promise<void>;
}

function permissionCount(count: number): string {
  return `${count} ${count === 1 ? "permission" : "permissions"}`;
}

/** Edits an existing role, or creates one when `role` is null. */
export function useRoleForm(role: Role | null, onSaved: () => void): RoleForm {
  const { show } = useToast();
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [selected, setSelected] = useState<string[]>(role?.permissions ?? []);
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function togglePermissions(permissions: Permission[], on: boolean): void {
    const keys = permissions.map((p) => p.key);
    setSelected((prev) => toggleKeys(prev, keys, on));
  }

  async function submit(): Promise<void> {
    setNameError(null);
    setError(null);
    if (!name.trim()) {
      setNameError("The role needs a name.");
      return;
    }
    setLoading(true);
    try {
      const body = { name: name.trim(), description: description.trim(), permissions: selected };
      if (role) await updateRole(role.id, body);
      else await createRole(body);
      show({ title: role ? "Role updated" : "Role created", text: `${body.name} has ${permissionCount(selected.length)}.`, tone: "success" });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.code === "role_name_taken") setNameError("A role with that name already exists.");
      else setError(errorMessage(err, "We couldn't save the role."));
    } finally {
      setLoading(false);
    }
  }

  return { name, setName, description, setDescription, selected, togglePermissions, nameError, error, loading, submit };
}
